import React, { useState, useEffect } from 'react';
import { 
  Users, 
  FileText, 
  CreditCard, 
  Activity, 
  Upload, 
  UserCheck, 
  Settings, 
  Cog, 
  HelpCircle, 
  LogOut,
  Menu,
  X,
  Heart,
  Shield,
  Clock,
  Award,
  ChevronDown,
  Search,
  Bell,
  User,
  Database,
  MessageSquare,
  Calendar,
  Briefcase,
  UserPlus,
  Eye,
  Key,
  ShieldCheck
} from 'lucide-react';
import PatientEntryForm from './PatientEntryForm';
import ViewPatient from './ViewPatient';
import Examination from './Examination';
import ExaminationPayment from './ExaminationPayment';
import ExaminationProcess from './ExaminationProcess';
import FileUploads from './FileUploads';
import NewDoctor from './NewDoctor';
import ViewPatientQuery from './ViewPatientQuery';
import RetrievePatientData from './RetrievePatientData';
import AddEmployee from './AddEmployee';
import ViewEmployee from './ViewEmployee';
import ViewMyAppointment from './ViewMyAppointment';
import Login from './Login';

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  
  // Load active section from localStorage or default to 'dashboard'
  const [activeSection, setActiveSection] = useState(() => {
    const saved = localStorage.getItem('activeSection');
    return saved ? saved : 'dashboard';
  });
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Save active section to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeSection', activeSection);
  }, [activeSection]);

  const handleLogin = (userData) => {
    setUserInfo(userData);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    setUserInfo(null);
    setIsLoggedIn(false);
    setActiveSection('dashboard');
  };

  const navItems = [
    { id: 'patient-entry', label: 'Patient Entry', icon: User },
    { id: 'view-patient', label: 'View Patient', icon: Users },
    { 
      id: 'examination', 
      label: 'Examination', 
      icon: FileText,
      subItems: [
        { id: 'examination', label: 'Examination', icon: FileText },
        { id: 'examination-payment', label: 'Payment', icon: CreditCard },
        { id: 'examination-process', label: 'Process', icon: Activity },
        { id: 'file-uploads', label: 'File Uploads', icon: Upload },
      ]
    },
    { 
      id: 'doctor-access', 
      label: 'Doctor Access', 
      icon: UserCheck,
      subItems: [
        { id: 'new-doctor', label: 'New Doctor', icon: User },
        { id: 'retrieve-patient-data', label: 'Retrieve Patient Data', icon: Database },
        { id: 'view-my-appointment', label: 'View My Appointment', icon: Calendar },
        { id: 'view-patient-query', label: 'View Patient Query', icon: MessageSquare }
      ]
    },
    { 
      id: 'employee', 
      label: 'Employee', 
      icon: Briefcase,
      subItems: [
        { id: 'add-employee', label: 'Add Employee', icon: UserPlus },
        { id: 'view-employee', label: 'View Employee', icon: Eye },
      ]
    },
    { 
      id: 'configuration', 
      label: 'Configuration', 
      icon: Cog,
      subItems: [
        { id: 'assign-roles', label: 'Assign Roles', icon: Key },
        { id: 'assign-rights', label: 'Assign Rights', icon: ShieldCheck },
      ]
    },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'support', label: 'Support', icon: HelpCircle },
  ];

  const toggleDropdown = (id) => {
    if (openDropdown === id) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(id);
    }
  };

  const renderDashboard = () => (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl p-8">
        <h1 className="text-4xl font-bold mb-4">Welcome to BlueRiz Diagnostic Centre</h1>
        <p className="text-xl opacity-90 mb-6">Advanced Healthcare Management System</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
            <Heart className="w-8 h-8 mb-2" />
            <h3 className="font-semibold">Patient Care</h3>
            <p className="text-sm opacity-80">Comprehensive patient management</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
            <Shield className="w-8 h-8 mb-2" />
            <h3 className="font-semibold">Secure Platform</h3>
            <p className="text-sm opacity-80">HIPAA compliant data protection</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
            <Award className="w-8 h-8 mb-2" />
            <h3 className="font-semibold">Quality Service</h3>
            <p className="text-sm opacity-80">Excellence in healthcare delivery</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Patients</p>
              <p className="text-2xl font-bold text-gray-800">1,247</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Today's Appointments</p>
              <p className="text-2xl font-bold text-gray-800">24</p>
            </div>
            <Clock className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Pending Reports</p>
              <p className="text-2xl font-bold text-gray-800">8</p>
            </div>
            <FileText className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Revenue Today</p>
              <p className="text-2xl font-bold text-gray-800">₹15,420</p>
            </div>
            <CreditCard className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium">New patient registered: John Doe</p>
              <p className="text-sm text-gray-500">2 minutes ago</p>
            </div>
          </div>
          <div className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium">Lab report completed for Sarah Johnson</p>
              <p className="text-sm text-gray-500">15 minutes ago</p>
            </div>
          </div>
          <div className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium">Payment received: ₹2,500</p>
              <p className="text-sm text-gray-500">1 hour ago</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (activeSection === 'dashboard') return renderDashboard();
    if (activeSection === 'patient-entry') {
      return <PatientEntryForm onBack={() => setActiveSection('dashboard')} />;
    }
    if (activeSection === 'view-patient') {
      return (
        <ViewPatient 
          onBack={() => setActiveSection('dashboard')}
          onViewPatient={(patient) => {
            console.log('View patient:', patient);
            alert(`Viewing patient: ${patient.firstName} ${patient.lastName}`);
          }}
          onEditPatient={(patient) => {
            console.log('Edit patient:', patient);
            alert(`Editing patient: ${patient.firstName} ${patient.lastName}`);
          }}
        />
      );
    }
    if (activeSection === 'examination') {
      return <Examination onBack={() => setActiveSection('dashboard')} />;
    }
    if (activeSection === 'examination-payment') {
    return <ExaminationPayment onBack={() => setActiveSection('dashboard')} />;
  }
  if (activeSection === 'examination-process') {
  return <ExaminationProcess onBack={() => setActiveSection('dashboard')} />;
}
if (activeSection === 'file-uploads') {
  return <FileUploads onBack={() => setActiveSection('dashboard')} />;
}
if (activeSection === 'new-doctor') {
  return <NewDoctor onBack={() => setActiveSection('dashboard')} />;
}
if (activeSection === 'view-patient-query') {
  return <ViewPatientQuery onBack={() => setActiveSection('dashboard')} />;
}
if (activeSection === 'retrieve-patient-data') {
  return <RetrievePatientData onBack={() => setActiveSection('dashboard')} />;
}
if (activeSection === 'add-employee') {
    return <AddEmployee onBack={() => setActiveSection('dashboard')} />;
  }
  if (activeSection === 'view-employee') {
    return <ViewEmployee onBack={() => setActiveSection('dashboard')} />;
  }
  if (activeSection === 'view-my-appointment') {
  return <ViewMyAppointment onBack={() => setActiveSection('dashboard')} />;
}

    return (
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {(() => {
              let iconComponent = null;
              for (const item of navItems) {
                if (item.id === activeSection) {
                  iconComponent = React.createElement(item.icon, {
                    className: "w-8 h-8 text-blue-600"
                  });
                  break;
                }
                if (item.subItems) {
                  const subItem = item.subItems.find(sub => sub.id === activeSection);
                  if (subItem) {
                    iconComponent = React.createElement(subItem.icon, {
                      className: "w-8 h-8 text-blue-600"
                    });
                    break;
                  }
                }
              }
              return iconComponent;
            })()}
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {(() => {
              for (const item of navItems) {
                if (item.id === activeSection) return item.label;
                if (item.subItems) {
                  const subItem = item.subItems.find(sub => sub.id === activeSection);
                  if (subItem) return subItem.label;
                }
              }
              return 'Section';
            })()}
          </h2>
          <p className="text-gray-600 mb-8">
            This section is ready for development. The interface will contain all necessary features for {(() => {
              for (const item of navItems) {
                if (item.id === activeSection) return item.label.toLowerCase();
                if (item.subItems) {
                  const subItem = item.subItems.find(sub => sub.id === activeSection);
                  if (subItem) return subItem.label.toLowerCase();
                }
              }
              return 'this section';
            })()}.
          </p>
          <div className="bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
            <h3 className="font-semibold text-gray-800 mb-2">Coming Soon</h3>
            <p className="text-sm text-gray-600">
              Advanced features and functionality will be implemented here based on your specific requirements.
            </p>
          </div>
        </div>
      </div>
    );
  };

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Navigation Bar - Made sticky */}
      <nav className="bg-gradient-to-r from-blue-600 to-blue-700 shadow-xl w-full sticky top-0 z-50">
        <div className="flex items-center h-16 w-full px-2 sm:px-6 lg:px-8">
          {/* Logo and Company Name */}
          <div className="flex items-center flex-shrink-0 mr-2 sm:mr-6">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-2">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-white leading-5">BlueRiz</h1>
              <p className="text-[10px] sm:text-xs text-blue-100 leading-4">Diagnostic Centre</p>
            </div>
          </div>
          
          {/* Spacer to push navigation to center */}
          <div className="flex-1 lg:hidden" />

          {/* Navigation Links - Centered */}
          <ul className="hidden lg:flex items-center justify-center flex-1 gap-x-2 xl:gap-x-3">
            <li>
              <button
                onClick={() => setActiveSection('dashboard')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeSection === 'dashboard'
                    ? 'bg-white/10 text-white shadow'
                    : 'text-blue-100 hover:text-white hover:bg-white/10'
                }`}
              >
                Dashboard
              </button>
            </li>
            {navItems.map(item =>
              item.subItems ? (
                <li key={item.id} className="relative group">
                  <button
                    onClick={() => toggleDropdown(item.id)}
                    className={`flex items-center gap-x-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                      activeSection.startsWith(item.id)
                        ? 'bg-white/10 text-white shadow'
                        : 'text-blue-100 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {/* Dropdown */}
                  {openDropdown === item.id && (
                    <ul className="absolute left-0 top-full mt-1 w-52 bg-white rounded-md shadow-md py-2 border border-blue-100 z-40">
                      {item.subItems.map(sub => (
                        <li key={sub.id}>
                          <button
                            onClick={() => {
                              setActiveSection(sub.id);
                              setOpenDropdown(null);
                            }}
                            className={`flex items-center gap-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded transition ${
                              activeSection === sub.id ? 'bg-blue-50 text-blue-800' : ''
                            }`}
                          >
                            <sub.icon className="w-4 h-4" />
                            <span>{sub.label}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ) : (
                <li key={item.id}>
                  <button
                    onClick={() => setActiveSection(item.id)}
                    className={`flex items-center gap-x-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                      activeSection === item.id
                        ? 'bg-white/10 text-white shadow'
                        : 'text-blue-100 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                </li>
              )
            )}
          </ul>

          {/* Spacer to push right-side icons to the right */}
          <div className="flex-1 lg:hidden" />

          {/* Right side icons */}
          <div className="hidden lg:flex items-center gap-x-2">
            <button className="p-2 text-blue-100 hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-300">
              <Search className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-x-2 bg-white/10 rounded-lg px-3 py-1.5">
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white font-medium text-xs">
                {userInfo?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <span className="text-white text-sm">
                {userInfo?.userType ? userInfo.userType.charAt(0).toUpperCase() + userInfo.userType.slice(1) : 'User'}
              </span>
            </div>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-x-1 px-2 py-2 rounded-lg text-xs font-medium text-blue-100 hover:text-white hover:bg-white/10 transition"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden ml-1">
            <button
              onClick={() => setIsMobileMenuOpen(v => !v)}
              className="p-2 rounded-lg text-blue-100 hover:text-white hover:bg-white/10 transition"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-blue-500 bg-blue-700">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button
                onClick={() => {
                  setActiveSection('dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className={`flex items-center space-x-2 w-full text-left px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                  activeSection === 'dashboard'
                    ? 'bg-white/20 text-white backdrop-blur-sm'
                    : 'text-blue-100 hover:text-white hover:bg-white/10'
                }`}
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <Heart className="w-5 h-5" />
                </div>
                <span>Dashboard</span>
              </button>
              
              {navItems.map((item) => {
                if (item.subItems) {
                  return (
                    <div key={item.id}>
                      <button
                        onClick={() => toggleDropdown(item.id)}
                        className={`flex items-center justify-between w-full text-left px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                          activeSection.startsWith(item.id)
                            ? 'bg-white/20 text-white backdrop-blur-sm'
                            : 'text-blue-100 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <item.icon className="w-5 h-5" />
                          <span>{item.label}</span>
                        </div>
                        <ChevronDown className={`w-5 h-5 transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {openDropdown === item.id && (
                        <div className="pl-6 mt-1 space-y-1">
                          {item.subItems.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <button
                                key={subItem.id}
                                onClick={() => {
                                  setActiveSection(subItem.id);
                                  setIsMobileMenuOpen(false);
                                  setOpenDropdown(null);
                                }}
                                className={`flex items-center space-x-2 w-full text-left px-3 py-2.5 rounded-lg text-base font-medium transition-colors ${
                                  activeSection === subItem.id
                                    ? 'bg-white/20 text-white backdrop-blur-sm'
                                    : 'text-blue-100 hover:text-white hover:bg-white/10'
                                }`}
                              >
                                <SubIcon className="w-5 h-5" />
                                <span>{subItem.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center space-x-2 w-full text-left px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                      activeSection === item.id
                        ? 'bg-white/20 text-white backdrop-blur-sm'
                        : 'text-blue-100 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
              
              <div className="pt-4 mt-4 border-t border-blue-500">
                <div className="flex items-center space-x-2 px-3 py-2 text-blue-100">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium">{userInfo?.username || 'User'}</p>
                    <p className="text-xs capitalize">{userInfo?.userType || 'User'}</p>
                  </div>
                </div>
                <button 
                  onClick={handleLogout}
                  className="flex items-center space-x-2 w-full text-left px-3 py-3 rounded-lg text-base font-medium text-blue-100 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content - Flex-grow to push footer down */}
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {renderContent()}
      </main>

      {/* Footer - Will always stay at bottom */}
      <footer className="bg-gray-800 text-white py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold">BlueRiz Diagnostic Centre</h3>
                <p className="text-sm text-gray-400">Excellence in Healthcare</p>
              </div>
            </div>
            <div className="text-sm text-gray-400">
              © 2025 BlueRiz Diagnostic Centre. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;